import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { primeAudio, playAlertBeep } from "@/lib/beep";

const POLL_MS = 20000;

// Same polling/watermark pattern as useInquiryAlerts, but keyed on booking
// creation time — /admin/appointments is sorted by start_time (soonest first),
// not created_at, so "newest" has to be computed rather than assumed from order.
export function useAppointmentAlerts(user) {
  const [appointments, setAppointments] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(true);
  const [soundArmed, setSoundArmed] = useState(false);

  const lastSeenRef = useRef(null);
  const initializedRef = useRef(false);
  const storageKey = user ? `eazy_last_seen_appt_${user.email}` : null;

  useEffect(() => {
    if (!storageKey) return;
    lastSeenRef.current = localStorage.getItem(storageKey);
    initializedRef.current = false;
  }, [storageKey]);

  const armSound = useCallback(() => {
    setSoundArmed(primeAudio());
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/admin/appointments");
        if (cancelled) return;
        const items = data.items || [];
        setAppointments(items);

        if (items.length > 0) {
          const newest = items.reduce((max, a) => (a.created_at > max ? a.created_at : max), items[0].created_at);

          if (!initializedRef.current) {
            initializedRef.current = true;
            if (!lastSeenRef.current) {
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          } else {
            const freshOnes = lastSeenRef.current
              ? items.filter((a) => a.created_at > lastSeenRef.current)
              : [];
            if (freshOnes.length > 0) {
              freshOnes.forEach((appt) => {
                toast.success(`New appointment from ${appt.name}`, {
                  description: new Date(appt.start_time).toLocaleString(),
                });
              });
              playAlertBeep();
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          }
        }
      } catch {
        // silent — avoid spamming errors from background polling
      } finally {
        if (!cancelled) setLoadingAppts(false);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user, storageKey]);

  return { appointments, setAppointments, loadingAppts, soundArmed, armSound };
}
